/** Client-side aggregates for merchant dashboard (sources: GET /api/orders, GET /api/orders/routes). */

export function parseMoney(v) {
  if (v == null) return 0;
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

export function parseDistanceKm(s) {
  if (s == null || s === "N/A") return 0;
  const m = String(s).match(/([\d.]+)/);
  return m ? Number(m[1]) : 0;
}

export function orderCreatedMs(o) {
  if (!o?.createdAt) return null;
  const t = Date.parse(o.createdAt);
  return Number.isFinite(t) ? t : null;
}

function localDayKey(ms) {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Calendar buckets for the last `windowDays` days (inclusive), browser-local date keys.
 * @returns {{ dateKey: string, label: string, orderCount: number, revenue: number }[]}
 */
export function buildDailyOrderBuckets(orders, windowDays = 30) {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);
  start.setDate(start.getDate() - (windowDays - 1));
  start.setHours(0, 0, 0, 0);

  const buckets = new Map();
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const key = localDayKey(d.getTime());
    buckets.set(key, { dateKey: key, label: key.slice(5), orderCount: 0, revenue: 0 });
  }

  for (const o of orders || []) {
    const ms = orderCreatedMs(o);
    if (ms == null || ms < start.getTime() || ms > end.getTime()) continue;
    const key = localDayKey(ms);
    const b = buckets.get(key);
    if (!b) continue;
    b.orderCount += 1;
    b.revenue += parseMoney(o.total);
  }

  return Array.from(buckets.values());
}

export function countByOrderStatus(orders) {
  const m = {};
  for (const o of orders || []) {
    const s = o.status || "UNKNOWN";
    m[s] = (m[s] || 0) + 1;
  }
  return m;
}

export function summarizeRoutes(routes) {
  let totalKm = 0;
  let sumDriverTotal = 0;
  let sumServiceFee = 0;
  let sumNetDriver = 0;
  let stopsOnRoutes = 0;
  const byStatus = {};

  for (const r of routes || []) {
    const st = r.status || "UNKNOWN";
    byStatus[st] = (byStatus[st] || 0) + 1;
    totalKm += parseDistanceKm(r.totalDistanceKm);
    sumDriverTotal += parseMoney(r.driverTotalEarning);
    sumServiceFee += parseMoney(r.serviceFeeEtb);
    sumNetDriver += parseMoney(r.netDriverPayoutEtb);
    const n = Array.isArray(r.orderedOrderIds) ? r.orderedOrderIds.length : 0;
    stopsOnRoutes += n;
  }

  const completedRoutes = byStatus.COMPLETED || 0;
  const activePipeline =
    (byStatus.DRAFT || 0) +
    (byStatus.CONFIRMED || 0) +
    (byStatus.BROADCASTED || 0) +
    (byStatus.ASSIGNED || 0);

  return {
    count: (routes || []).length,
    totalKm,
    sumDriverTotal,
    sumServiceFee,
    sumNetDriver,
    stopsOnRoutes,
    byStatus,
    completedRoutes,
    activePipeline,
  };
}

export function ordersTotals(orders) {
  const list = orders || [];
  let gmv = 0;
  let delivered = 0;
  let cancelled = 0;
  for (const o of list) {
    gmv += parseMoney(o.total);
    if (o.status === "DELIVERED") delivered += 1;
    if (o.status === "CANCELLED") cancelled += 1;
  }
  const n = list.length;
  return {
    count: n,
    gmv,
    avgOrderValue: n > 0 ? gmv / n : 0,
    delivered,
    cancelled,
    deliveredRate: n > 0 ? delivered / n : 0,
  };
}

/** Status → sort rank for pipeline display */
const ROUTE_STATUS_ORDER = ["DRAFT", "CONFIRMED", "BROADCASTED", "ASSIGNED", "COMPLETED"];

export function routesStatusSeries(byStatus) {
  return ROUTE_STATUS_ORDER.filter((s) => (byStatus[s] || 0) > 0).map((status) => ({
    status,
    count: byStatus[status] || 0,
  }));
}

const ORDER_STATUS_PALETTE = {
  CREATED: "#5e5646",
  READY: "#1f4e79",
  PLANNED: "#6b4eaa",
  CONFIRMED: "#6b4f9e",
  BROADCASTED: "#b8955a",
  PICKED_UP: "#94490f",
  DELIVERED: "#2f7a57",
  CANCELLED: "#a63a3a",
  UNKNOWN: "#8c8595",
};

export function orderStatusPieData(counts) {
  return Object.entries(counts || {}).map(([name, value]) => ({
    name,
    value,
    color: ORDER_STATUS_PALETTE[name] || ORDER_STATUS_PALETTE.UNKNOWN,
  }));
}
